import { Fragment, memo, useMemo, type ReactNode } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@trillion/ui/components/ui/sheet";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Button } from "@trillion/ui/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@trillion/ui/components/ui/toggle-group";
import { useAutoScroll } from "@trillion/ui/hooks/use-auto-scroll";
import { useReplayLog } from "@/hooks/use-replay-log";
import { useReplayPlayer } from "@/hooks/use-replay-player";
import { useRunTail } from "@/hooks/use-run-tail";
import type { RunLogEntry } from "@/lib/jobs-types";
import { logLevelDisplay } from "@/lib/log-levels";
import { REPLAY_SPEEDS, buildReplaySchedule, type ReplaySpeed } from "@/lib/replay-scheduler";
import { formatProgress } from "@/lib/run-progress";
import { tailNotices, type WorkerNotice } from "@/lib/run-tail";
import { fmt } from "./run-columns";

interface RunLogPanelProps {
  /** The run to show; the sheet is open while non-null. */
  jobId: string | null;
  /** `live` tails an in-flight run (sc-114197); `replay` plays a finished run's log back (sc-114198). */
  mode?: "live" | "replay";
  onOpenChange: (open: boolean) => void;
}

// Live log tail + progress for one in-flight run (sc-114197), or a paced replay of a finished run's log
// (sc-114198), mirroring ErrorInspector's Sheet. The inner view is keyed by the job id, so each open gets a
// fresh load (and a replay starts again at 1×) and a late page for a previous run lands nowhere; it
// unmounts on close, which stops polling or playback.
export function RunLogPanel({ jobId, mode = "live", onOpenChange }: RunLogPanelProps) {
  return (
    <Sheet open={jobId !== null} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{mode === "replay" ? "Log replay" : "Run log"}</SheetTitle>
          <SheetDescription className="font-mono text-xs">{jobId}</SheetDescription>
        </SheetHeader>

        {jobId !== null &&
          (mode === "replay" ? (
            <RunReplayView key={jobId} jobId={jobId} />
          ) : (
            <RunLogView key={jobId} jobId={jobId} />
          ))}
      </SheetContent>
    </Sheet>
  );
}

/** The scrolling `role="log"` container both views share, auto-scrolling while the operator is at the bottom. */
function LogScroller({ itemCount, children }: { itemCount: number; children: ReactNode }) {
  const { containerRef, handleScroll } = useAutoScroll([itemCount]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      role="log"
      aria-label="Run log"
      className="min-h-0 flex-1 overflow-y-auto rounded border bg-muted/30 p-3 font-mono text-xs"
    >
      {children}
    </div>
  );
}

/** One log line, identical in both views. Memoised, so a replay step never re-renders lines already shown. */
const LogEntryItem = memo(function LogEntryItem({ entry }: { entry: RunLogEntry }) {
  const level = logLevelDisplay(entry.log_level_id);
  return (
    <li title={entry.category}>
      <span className="text-muted-foreground">{fmt(entry.logged_at)}</span>{" "}
      <span className={level.className}>{level.label}</span>{" "}
      <span className="whitespace-pre-wrap break-words">{entry.message}</span>
      {entry.exception && (
        <pre className="mt-1 whitespace-pre-wrap break-words text-destructive">
          {entry.exception}
        </pre>
      )}
    </li>
  );
});

function workerNoticeText(notice: WorkerNotice): string {
  return notice.reason === "orphaned"
    ? "Worker may be gone — its server is no longer registered with Hangfire."
    : `Worker may be gone — no heartbeat for ${notice.staleForSeconds} s.`;
}

/**
 * The live tail's view — progress, the dead-worker and quiet notices, and the scrolling log — for the Sheet
 * above and, inline, for the job details page (sc-114201). It polls until its own tail reports `finished`.
 */
export function RunLogView({ jobId }: { jobId: string }) {
  const tail = useRunTail(jobId);
  const { entries, progress, finished, isLoading, nowMs } = tail;
  const { quietForSeconds, workerMayBeGone } = tailNotices(tail, nowMs);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-6">
      <section className="flex items-center gap-2">
        <p className="text-sm font-medium">
          {progress ? formatProgress(progress) : "No progress reported yet"}
        </p>
        {finished && <Badge variant="outline">Run finished</Badge>}
      </section>

      {workerMayBeGone && (
        <div
          role="alert"
          className="rounded border border-destructive/50 px-3 py-2 text-sm text-destructive"
        >
          {workerNoticeText(workerMayBeGone)}
        </div>
      )}

      {quietForSeconds !== null && (
        <p className="text-xs text-muted-foreground">{`No output for ${quietForSeconds} s`}</p>
      )}

      <LogScroller itemCount={entries.length}>
        {entries.length ? (
          <ol className="flex flex-col gap-1">
            {entries.map((entry) => (
              <LogEntryItem key={entry.log_id} entry={entry} />
            ))}
          </ol>
        ) : (
          <p className="text-muted-foreground">{isLoading ? "Loading…" : "No output yet."}</p>
        )}
      </LogScroller>
    </div>
  );
}

const NOT_FINAL_NOTICE =
  "This run finished moments ago, so its log may still be completing. Reopen the replay to load the rest.";

/**
 * Plays a finished run's complete log back at its recorded pace (sc-114198): the whole log loads first,
 * then entries appear on the schedule's timers. A compressed gap shows as `⋯ N s` in the same render as
 * the entry that closed it. Progress is not replayed (one mutable row, no history).
 */
function RunReplayView({ jobId }: { jobId: string }) {
  const log = useReplayLog(jobId);
  const schedule = useMemo(
    () => (log.status === "ready" ? buildReplaySchedule(log.entries) : null),
    [log]
  );
  const player = useReplayPlayer(schedule);
  const total = log.entries.length;
  const controlsDisabled = log.status !== "ready" || total === 0;

  let body: ReactNode;
  if (log.status === "loading") {
    body = <p className="text-muted-foreground">Loading…</p>;
  } else if (log.status === "error") {
    body = <p className="text-muted-foreground">Could not load the run log.</p>;
  } else if (total === 0 || schedule === null) {
    body = <p className="text-muted-foreground">No output recorded.</p>;
  } else {
    body = (
      <ol className="flex flex-col gap-1">
        {log.entries.slice(0, player.shown).map((entry, index) => {
          const gapSeconds = schedule.steps[index].gapSeconds;
          return (
            <Fragment key={entry.log_id}>
              {gapSeconds !== null && (
                <li data-gap="" className="text-muted-foreground">{`⋯ ${gapSeconds} s`}</li>
              )}
              <LogEntryItem entry={entry} />
            </Fragment>
          );
        })}
      </ol>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-6">
      <section className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={player.togglePlaying}
          disabled={controlsDisabled || player.ended}
        >
          {player.playing ? "Pause" : "Play"}
        </Button>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          aria-label="Playback speed"
          value={String(player.speed)}
          onValueChange={(value) => {
            // Clicking the pressed item emits "": keep the current speed.
            if (value) player.changeSpeed(Number(value) as ReplaySpeed);
          }}
          disabled={controlsDisabled}
        >
          {REPLAY_SPEEDS.map((speed) => (
            <ToggleGroupItem key={speed} value={String(speed)}>
              {`${speed}×`}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Button
          variant="outline"
          size="sm"
          onClick={player.skipToEnd}
          disabled={controlsDisabled || player.ended}
        >
          Skip to end
        </Button>
        <p className="text-sm text-muted-foreground">{`${player.shown} / ${total} lines`}</p>
      </section>

      {log.status === "ready" && !log.final && (
        <p role="status" className="text-xs text-muted-foreground">
          {NOT_FINAL_NOTICE}
        </p>
      )}

      <LogScroller itemCount={player.shown}>{body}</LogScroller>
    </div>
  );
}
